/**
 * Iter 115 — Required post-signup branding wizard.
 *
 * When a fresh admin lands on this page they must upload a logo,
 * enter a tagline, and pick a primary color before they can reach
 * the dashboard. The guard sits in App.js — `NeedsBrandingGuard`
 * redirects any admin whose clinic has no clinic_logo_url and no
 * clinic_tagline back here before rendering `/admin`.
 *
 * All three fields are REQUIRED (per user's Iter 115 choice a).
 * Logo is stored as base64 in `settings.clinic_logo_url` (choice a).
 * Color is a 8 preset palette + free hex picker (choice b).
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Upload, ChevronRight, Sparkles, AlertCircle } from 'lucide-react';

// Curated palette — 8 professional colors that pair well with white/cream.
const COLOR_PRESETS = [
  { name: 'Ocean',      hex: '#003D80' },
  { name: 'Forest',     hex: '#0F7B4F' },
  { name: 'Coral',      hex: '#E24A33' },
  { name: 'Amber',      hex: '#B76E00' },
  { name: 'Plum',       hex: '#6B2C91' },
  { name: 'Slate',      hex: '#1F3A5F' },
  { name: 'Rose',       hex: '#C43A6E' },
  { name: 'Teal',       hex: '#0F7B7A' },
];

const HEX_RE = /^#([0-9a-f]{6})$/i;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function OnboardBranding() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [tagline, setTagline] = useState('');
  const [color, setColor] = useState(COLOR_PRESETS[0].hex);
  const [colorMode, setColorMode] = useState('preset'); // 'preset' | 'custom'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [clinicName, setClinicName] = useState('');

  // Load only the clinic NAME for personalisation. Do NOT prefill logo/tagline/color
  // from /clinic/settings when the clinic hasn't set custom branding — the backend
  // returns platform DEFAULT_SETTINGS fallback ('/logo.jpg', 'Recover · Rehab · Train')
  // for un-branded tenants and pre-populating those would (a) confuse the user, and
  // (b) let them accidentally submit the platform defaults, defeating the wizard.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/clinic/settings');
        setClinicName(data.clinic_name || '');
        // Only prefill fields when this admin actually already saved custom branding.
        if (data._has_custom_branding) {
          if (data.clinic_logo_url) setLogoDataUrl(data.clinic_logo_url);
          if (data.clinic_tagline) setTagline(data.clinic_tagline);
          if (data.brand_primary_color) {
            setColor(data.brand_primary_color);
            const preset = COLOR_PRESETS.find(p => p.hex.toLowerCase() === data.brand_primary_color.toLowerCase());
            setColorMode(preset ? 'preset' : 'custom');
          }
        }
      } catch (_) { /* first-run — nothing to pre-fill */ }
    })();
  }, []);

  const handleFile = async (e) => {
    setError('');
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPG, SVG).');
      return;
    }
    if (f.size > 512 * 1024) {
      setError('Logo must be under 512 KB. Please resize before uploading.');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(f);
      setLogoDataUrl(dataUrl);
    } catch (_) {
      setError('Could not read that image. Try a different file.');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!logoDataUrl) return setError('Please upload your clinic logo.');
    if (!tagline.trim()) return setError('Please enter a short tagline (e.g. "Sports rehab specialists").');
    if (!HEX_RE.test(color)) return setError('Please pick a valid color (or type a #RRGGBB hex value).');

    setBusy(true);
    try {
      await api.put('/clinic/settings', {
        clinic_logo_url: logoDataUrl,
        clinic_tagline: tagline.trim(),
        brand_primary_color: color,
      });
      // Force a full reload so /clinic/settings is re-fetched by AppLayout
      // and the newly-branded sidebar appears immediately.
      window.location.assign('/admin');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save branding. Try again.');
      setBusy(false);
    }
  };

  const previewName = clinicName || 'Your clinic';

  return (
    <div className="min-h-screen bg-[#FAF6EE] flex items-center justify-center px-4 py-10" data-testid="onboard-branding-page">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#003D80]/10 text-[#003D80] text-xs uppercase tracking-[0.15em] font-medium">
            <Sparkles className="w-3.5 h-3.5" /> Step 2 of 2 · Brand your clinic
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold mt-4 text-[#003D80]" data-testid="branding-title">
            Make it yours, {previewName}.
          </h1>
          <p className="text-base text-fg-muted mt-3 max-w-xl mx-auto">
            Upload your logo, choose your tagline and pick a primary colour — your patients will see this everywhere, from booking pages to invoices.
          </p>
        </div>

        <form onSubmit={submit} className="grid md:grid-cols-2 gap-6">
          {/* LEFT: form fields */}
          <div className="bg-white border border-soft rounded-2xl p-6 space-y-5">
            {/* Logo */}
            <div>
              <label className="block text-sm font-semibold text-fg mb-1.5">Clinic logo *</label>
              <div className="flex items-center gap-4">
                <div
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-[#003D80]/30 bg-[#003D80]/5 flex items-center justify-center overflow-hidden shrink-0"
                  data-testid="branding-logo-preview"
                >
                  {logoDataUrl ? (
                    <img src={logoDataUrl} alt="Logo preview" className="w-full h-full object-contain" />
                  ) : (
                    <Upload className="w-6 h-6 text-[#003D80]/40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleFile}
                    className="hidden"
                    data-testid="branding-logo-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="text-sm px-3 py-1.5 rounded-md bg-[#003D80] text-white hover:bg-[#002A5A] transition"
                    data-testid="branding-logo-choose"
                  >
                    {logoDataUrl ? 'Change logo' : 'Choose logo'}
                  </button>
                  <p className="text-xs text-fg-muted mt-1.5 leading-snug">PNG, JPG or SVG · up to 512 KB · square shape works best.</p>
                </div>
              </div>
            </div>

            {/* Tagline */}
            <div>
              <label className="block text-sm font-semibold text-fg mb-1.5">Tagline *</label>
              <input
                className="input w-full"
                value={tagline}
                onChange={e => setTagline(e.target.value.slice(0, 60))}
                placeholder="e.g. Sports rehab specialists"
                maxLength={60}
                required
                data-testid="branding-tagline-input"
              />
              <p className="text-xs text-fg-muted mt-1">{tagline.length}/60 chars · Shown under your logo in the sidebar.</p>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-semibold text-fg mb-1.5">Primary colour *</label>
              <div className="grid grid-cols-8 gap-2" data-testid="branding-color-presets">
                {COLOR_PRESETS.map(p => (
                  <button
                    key={p.hex}
                    type="button"
                    onClick={() => { setColor(p.hex); setColorMode('preset'); }}
                    aria-label={p.name}
                    title={p.name}
                    className={`w-full aspect-square rounded-lg border-2 transition ${color === p.hex && colorMode === 'preset' ? 'border-fg scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: p.hex }}
                    data-testid={`branding-color-${p.name.toLowerCase()}`}
                  />
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="color"
                  value={HEX_RE.test(color) ? color : '#003D80'}
                  onChange={e => { setColor(e.target.value); setColorMode('custom'); }}
                  className="w-10 h-10 rounded border border-soft cursor-pointer"
                  aria-label="Custom colour"
                  data-testid="branding-color-picker"
                />
                <input
                  type="text"
                  value={color}
                  onChange={e => {
                    const v = e.target.value.trim();
                    setColor(v.startsWith('#') ? v : `#${v}`);
                    setColorMode('custom');
                  }}
                  className="input flex-1 font-mono text-sm"
                  placeholder="#003D80"
                  maxLength={7}
                  data-testid="branding-color-hex"
                />
              </div>
            </div>

            {error && (
              <div className="border border-[#B83A3A]/30 bg-[#B83A3A]/5 rounded-md p-3 flex items-start gap-2" data-testid="branding-error">
                <AlertCircle className="w-4 h-4 text-[#B83A3A] shrink-0 mt-0.5" />
                <div className="text-sm text-[#B83A3A]">{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#003D80] text-white font-semibold hover:bg-[#002A5A] disabled:opacity-60 transition"
              data-testid="branding-submit"
            >
              {busy ? 'Saving…' : 'Save & continue to dashboard'}
              <ChevronRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] text-fg-muted text-center">Required · you can update any of these later under Settings → Branding.</p>
          </div>

          {/* RIGHT: live preview */}
          <div className="bg-white border border-soft rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-fg-muted font-medium mb-4">Live preview</div>

            {/* Sidebar-brand preview */}
            <div className="border border-soft rounded-xl p-4 flex items-center gap-3 mb-4" style={{ backgroundColor: `${color}0d` }} data-testid="branding-preview-sidebar">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-soft shrink-0">
                {logoDataUrl ? (
                  <img src={logoDataUrl} alt="" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-xs text-fg-muted">Logo</div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold tracking-tight text-fg leading-tight truncate">{previewName}</div>
                <div className="text-[10px] text-fg-muted -mt-0.5 uppercase tracking-wider truncate">{tagline || 'Your tagline here'}</div>
              </div>
            </div>

            {/* Button preview */}
            <div className="border border-soft rounded-xl p-4 mb-4">
              <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-2">Primary button</div>
              <div
                className="inline-block px-4 py-2 rounded-md text-white text-sm font-semibold"
                style={{ backgroundColor: color }}
                data-testid="branding-preview-button"
              >
                Book a session
              </div>
            </div>

            {/* Invoice/email header preview */}
            <div className="border border-soft rounded-xl overflow-hidden">
              <div className="p-3 flex items-center gap-2" style={{ backgroundColor: color }} data-testid="branding-preview-header">
                <div className="w-8 h-8 rounded bg-white/95 flex items-center justify-center overflow-hidden shrink-0">
                  {logoDataUrl && <img src={logoDataUrl} alt="" className="w-full h-full object-contain" />}
                </div>
                <div className="text-white font-semibold text-sm truncate">{previewName}</div>
              </div>
              <div className="p-3 text-xs text-fg-muted">
                Invoice #INV-2026-001<br />
                Patient email header preview.
              </div>
            </div>

            <p className="text-[11px] text-fg-muted text-center mt-4 leading-snug">Your logo appears on the sidebar, patient emails, invoices, PDF exports and public booking pages.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
